import React, { useState } from 'react';
import { Lock, Save, Eye, EyeOff } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

interface ProfileSecurityProps {
  onChangePassword: (data: { current_password: string; new_password: string }) => Promise<void>;
  loading: boolean;
}

export default function ProfileSecurity({ onChangePassword, loading }: ProfileSecurityProps) {
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.current_password) {
      newErrors.current_password = 'Password saat ini harus diisi';
    }

    if (!formData.new_password) {
      newErrors.new_password = 'Password baru harus diisi';
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = 'Password minimal 8 karakter';
    }

    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Konfirmasi password harus diisi';
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = 'Password tidak cocok';
    }

    if (formData.current_password === formData.new_password) {
      newErrors.new_password = 'Password baru harus berbeda dengan password saat ini';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      await onChangePassword({
        current_password: formData.current_password,
        new_password: formData.new_password,
      });
      // Reset form after successful submission
      setFormData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <div className="relative">
      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center rounded-lg">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            <p className="text-blue-600 font-medium text-lg">Mengubah password...</p>
            <p className="text-gray-500 text-sm">Mohon tunggu sebentar</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Keamanan Password</h3>
              <p className="text-sm text-blue-700 mt-1">
                Gunakan password yang kuat dan jangan bagikan dengan siapa pun. Disarankan
                menggunakan kombinasi huruf, angka, dan simbol.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Current Password */}
          <div className="space-y-2">
            <Label htmlFor="current_password">Password Saat Ini *</Label>
            <div className="relative">
              <Input
                id="current_password"
                type={showPasswords.current ? 'text' : 'password'}
                value={formData.current_password}
                onChange={e => handleChange('current_password', e.target.value)}
                required
                className={`focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
                  errors.current_password ? 'border-red-500' : ''
                }`}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPasswords.current ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.current_password && (
              <p className="text-sm text-red-600">{errors.current_password}</p>
            )}
          </div>

          {/* New Password */}
          <div className="space-y-2">
            <Label htmlFor="new_password">Password Baru *</Label>
            <div className="relative">
              <Input
                id="new_password"
                type={showPasswords.new ? 'text' : 'password'}
                value={formData.new_password}
                onChange={e => handleChange('new_password', e.target.value)}
                required
                className={`focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
                  errors.new_password ? 'border-red-500' : ''
                }`}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.new_password && <p className="text-sm text-red-600">{errors.new_password}</p>}
            <p className="text-xs text-gray-500">Minimal 8 karakter</p>
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <Label htmlFor="confirm_password">Konfirmasi Password Baru *</Label>
            <div className="relative">
              <Input
                id="confirm_password"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirm_password}
                onChange={e => handleChange('confirm_password', e.target.value)}
                required
                className={`focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
                  errors.confirm_password ? 'border-red-500' : ''
                }`}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPasswords.confirm ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
            {errors.confirm_password && (
              <p className="text-sm text-red-600">{errors.confirm_password}</p>
            )}
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Mengubah Password...' : 'Ubah Password'}
          </Button>
        </div>
      </form>
    </div>
  );
}
